import React, { useMemo, useState } from "react";
import { Award, Check, Search } from "lucide-react";
import AddButton from "@/components/Core/AddButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CertificationSelectOption {
	key: string;
	/** Display name (already resolved to the current locale). */
	name: string;
}

interface CertificationSelectPopoverProps {
	/** All selectable certification types. */
	options: CertificationSelectOption[];
	/** Keys of the certification types currently selected. */
	selectedKeys: string[];
	/** Select a certification type. */
	onSelect: (key: string) => void;
	/** Deselect a certification type. */
	onDeselect: (key: string) => void;
	/** Dashed trigger button label. Defaults to "Lägg till certifikat". */
	triggerLabel?: string;
	/** Heading shown above the list. Defaults to "Certifikat". */
	heading?: string;
	/** Search field placeholder. Defaults to "Sök certifikat…". */
	searchPlaceholder?: string;
	disabled?: boolean;
	align?: "start" | "center" | "end";
	/** Extra classes merged onto the dashed trigger button (e.g. to shrink it). */
	triggerClassName?: string;
	/** Size of the trigger's leading plus icon in px. */
	triggerIconSize?: number;
	/**
	 * Custom trigger element, rendered via `PopoverTrigger asChild` instead of the default dashed
	 * {@link AddButton}. Must be a single element that forwards refs/props.
	 */
	trigger?: React.ReactNode;
}

/**
 * Certification counterpart of {@link TagSelectPopover}: a dashed {@link AddButton}
 * that opens a popover listing every certification type as a plain row (no tag
 * color) with a checkbox. Checking selects, unchecking removes — toggling many
 * without closing. A search box filters long taxonomies. Domain-free: pass
 * resolved `{ key, name }` options plus select/deselect callbacks; the caller
 * owns persistence.
 */
const CertificationSelectPopover: React.FC<CertificationSelectPopoverProps> = ({
	options,
	selectedKeys,
	onSelect,
	onDeselect,
	triggerLabel = "Lägg till certifikat",
	heading = "Certifikat",
	searchPlaceholder = "Sök certifikat…",
	disabled,
	align = "start",
	triggerClassName,
	triggerIconSize,
	trigger,
}) => {
	const [query, setQuery] = useState("");

	const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return options;
		return options.filter((o) => o.name.toLowerCase().includes(q));
	}, [options, query]);

	return (
		<Popover>
			<PopoverTrigger asChild>
				{trigger ?? (
					<AddButton
						label={triggerLabel}
						disabled={disabled}
						className={triggerClassName}
						iconSize={triggerIconSize}
					/>
				)}
			</PopoverTrigger>
			<PopoverContent align={align} className="w-72 p-0">
				<div className="border-b border-gray-200 p-2.5">
					<div className="relative">
						<Search
							size={14}
							className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
						/>
						<input
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={searchPlaceholder}
							autoFocus
							className="w-full rounded-md border border-solid border-gray-300 bg-white py-1.5 pl-8 pr-2.5 text-sm text-gray-800 outline-none focus:border-brand"
						/>
					</div>
				</div>

				<div className="max-h-72 overflow-y-auto p-2">
					<p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
						{heading}
					</p>
					{filtered.length === 0 ? (
						<p className="px-1 py-2 text-sm text-gray-500">Inga certifikat matchar.</p>
					) : (
						<ul className="flex flex-col gap-1">
							{filtered.map((option) => {
								const isSelected = selected.has(option.key);
								return (
									<li key={option.key}>
										<button
											type="button"
											role="checkbox"
											aria-checked={isSelected}
											disabled={disabled}
											onClick={() => (isSelected ? onDeselect(option.key) : onSelect(option.key))}
											className="flex w-full items-center gap-2 rounded border-0 bg-transparent p-0 text-left hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
										>
											<span
												className={cn(
													"flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
													isSelected
														? "border-brand bg-brand text-white"
														: "border-gray-300 bg-white"
												)}
											>
												{isSelected && <Check size={12} strokeWidth={3} />}
											</span>
											<span className="flex min-w-0 flex-1 items-center gap-2 truncate rounded px-3 py-1.5 text-sm font-medium text-gray-800">
												<Award size={14} className="shrink-0 text-gray-500" />
												<span className="truncate">{option.name}</span>
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default CertificationSelectPopover;
